import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { searchActivities, getActivityDetail, getActivityFilterOptions, getActivityPhases, getPickerOptions } from '../requests/api';

const getData = (res) => res?.data ?? res;

export const loadActivities = createAsyncThunk(
	'activity/search',
	async (params, { rejectWithValue }) => {
		try { return getData(await searchActivities(params)); }
		catch (e) { return rejectWithValue(e.response?.data ?? e.message); }
	}
);

// Full Vendor/Technology universe from the DB, scoped by privilege — fetched
// once. The picker derives the Vendor -> Technology cascade in memory from
// the combo list, so no further calls are needed when Vendor changes.
export const loadPickerOptions = createAsyncThunk(
	'activity/pickerOptions',
	async (_, { rejectWithValue }) => {
		try { return getData(await getPickerOptions()); }
		catch (e) { return rejectWithValue(e.response?.data ?? e.message); }
	}
);

export const loadActivityDetail = createAsyncThunk(
	'activity/detail',
	async (id, { rejectWithValue }) => {
		try { return getData(await getActivityDetail(id)); }
		catch (e) { return rejectWithValue(e.response?.data ?? e.message); }
	}
);

export const loadFilterOptions = createAsyncThunk(
	'activity/filterOptions',
	async ({ id, filters }, { rejectWithValue }) => {
		try { return getData(await getActivityFilterOptions(id, filters)); }
		catch (e) { return rejectWithValue(e.response?.data ?? e.message); }
	}
);

export const loadActivityPhases = createAsyncThunk(
	'activity/phases',
	async (id, { rejectWithValue }) => {
		try { return getData(await getActivityPhases(id)); }
		catch (e) { return rejectWithValue(e.response?.data ?? e.message); }
	}
);

const activitySlice = createSlice({
	name: 'activity',
	initialState: {
		list:           [],
		listLoading:    false,
		listError:      null,
		selected:       null,
		detailLoading:  false,
		detailError:    null,
		filterOptions:  { operators: [], vendors: [], siteIds: [], jvIds: [] },
		optionsLoading: false,
		phases:         [],   // { phaseId, label, date, isRollback, sortOrder }
		phasesLoading:  false,
		pickerOptions:        { combos: [] },   // [{ vendor, technology }, ...]
		pickerOptionsLoading: false,
	},
	reducers: {
		clearSelected(state) { state.selected = null; state.phases = []; },
	},
	extraReducers: b => {
		b.addCase(loadActivities.pending,   s => { s.listLoading = true;  s.listError = null; })
			.addCase(loadActivities.fulfilled, (s, a) => { s.listLoading = false; s.list = a.payload; })
			.addCase(loadActivities.rejected,  (s, a) => { s.listLoading = false; s.listError = a.payload; });

		b.addCase(loadActivityDetail.pending,   s => { s.detailLoading = true; s.detailError = null; })
			.addCase(loadActivityDetail.fulfilled, (s, a) => { s.detailLoading = false; s.selected = a.payload; })
			.addCase(loadActivityDetail.rejected,  (s, a) => { s.detailLoading = false; s.detailError = a.payload; });

		b.addCase(loadFilterOptions.pending,   s => { s.optionsLoading = true; })
			.addCase(loadFilterOptions.fulfilled, (s, a) => { s.optionsLoading = false; s.filterOptions = a.payload; })
			.addCase(loadFilterOptions.rejected,  s => { s.optionsLoading = false; });

		b.addCase(loadActivityPhases.pending,   s => { s.phasesLoading = true; })
			.addCase(loadActivityPhases.fulfilled, (s, a) => { s.phasesLoading = false; s.phases = a.payload; })
			.addCase(loadActivityPhases.rejected,  s => { s.phasesLoading = false; });

		b.addCase(loadPickerOptions.pending,   s => { s.pickerOptionsLoading = true; })
			.addCase(loadPickerOptions.fulfilled, (s, a) => { s.pickerOptionsLoading = false; s.pickerOptions = a.payload; })
			.addCase(loadPickerOptions.rejected,  s => { s.pickerOptionsLoading = false; });
	},
});

export const { clearSelected } = activitySlice.actions;
export default activitySlice.reducer;